// src/components/ProtectedRoute/Admin/Dialogs/parts/PublicationSelector.jsx
"use client";

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Autocomplete, TextField, Stack, Button, Box, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useDebounce } from "use-debounce";
import supabase from "../../../../../utils/supabase";
import { selectInBatched } from "../../../../../utils/supabase-batch";
import DialogPublicationUpsert from "../DialogPublicationUpsert";
import { useMasterData } from "../../../../../utils/MasterDataProvider";

const TAG = "[PublicationSelector]";

const isUuid = (s) =>
  typeof s === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);

const isIntLike = (v) =>
  (typeof v === "number" && Number.isInteger(v)) ||
  (typeof v === "string" && /^\d+$/.test(v));

/* ---------------------- utils ---------------------- */
/** HTMLタグ除去 + エンティティ復号（安全にプレーンテキスト化） */
function stripHtmlAndDecode(input) {
  if (!input) return "";
  const noTags = String(input).replace(/<[^>]*>/g, " ");
  const el = document.createElement("textarea");
  el.innerHTML = noTags;
  return el.value.replace(/\s+/g, " ").trim();
}

/** 著者配列を "A", "A & B", "A, B & C" 形式に */
function formatAuthors(authors = []) {
  const a = authors.filter(Boolean);
  if (a.length <= 1) return a[0] || "";
  if (a.length === 2) return `${a[0]} & ${a[1]}`;
  return `${a.slice(0, -1).join(", ")} & ${a[a.length - 1]}`;
}

const parseAuthorsStr = (s) =>
  (s || "")
    .split(/[,;]+/)
    .map((x) => x.trim())
    .filter(Boolean);

const buildCitation = (row) => {
  const authors =
    Array.isArray(row._authors) && row._authors.length
      ? formatAuthors(row._authors)
      : parseAuthorsStr(row.authors_str).join("; ");
  const year = row.publication_date ? new Date(row.publication_date).getFullYear() : "n.d.";
  const title =
    stripHtmlAndDecode(row.title_english || row.title || row.title_original || "[No Title]");
  const journal =
    stripHtmlAndDecode(row._journal_name || row.journal_hint || row.journal_name || "");
  const vol = row.volume ? ` ${row.volume}` : "";
  const num = row.number ? `(${row.number})` : "";
  const page = row.page ? `: ${row.page}` : "";
  const doi = row.doi ? ` doi:${row.doi}` : "";
  return `${authors ? `${authors} ` : ""}(${year}). ${title}${
    journal ? ` — ${journal}${vol}${num}${page}` : ""
  }${doi}`;
};

// or= 用の簡易 ilike ビルダー（ワイルドカードは %）
const buildOrIlike = (cols, term) => {
  // スペースを %20 にして 400 (Bad Request) を防ぐ
  const safe = String(term || "").replace(/ /g, "%20");
  const pat = `%${safe}%`;
  return cols.map((c) => `${c}.ilike.${pat}`).join(",");
};

// RPC 名（Supabase 側に作成することを想定）
const RPC_FUZZY = "search_publications_fuzzy";

// publications 取得時に共通で使う select 句
const BASE_SELECT = `
  id,
  id_uuid,
  title_english,
  publication_date,
  journal_id,
  publications_authors!left (
    author_order,
    researcher:researcher_id ( id, first_name, last_name )
  )
`;

/** デフォルトのラベル文字列（必ず string を返す） */
function defaultLabel(p) {
  if (!p) return "";
  const cit = p._citation || p.citation;
  return cit || buildCitation(p);
}


/* ---------- 付加情報ハイドレーション（安全版） ---------- */
function extractAuthorsFromRel(rel = []) {
  return rel
    .map((r) => ({
      order: r?.author_order ?? 0,
      name:
        [r?.researcher?.last_name, r?.researcher?.first_name]
          .filter(Boolean)
          .join(", ") ||
        [r?.author?.last_name, r?.author?.first_name]
          .filter(Boolean)
          .join(", ") ||
        "",
    }))
    .filter((r) => r.name)
    .sort((a, b) => a.order - b.order)
    .map((x) => x.name);
}

async function fetchAuthorsForPublication(pubId) {
  if (!pubId) return [];
  const middleCandidates = ["publications_authors", "publication_authors"]; // 本命→publications_authors
  for (const table of middleCandidates) {
    try {
      const { data: rel, error } = await supabase
        .from(table)
        .select("author_order, publication_id, publication_id_uuid, researcher:researcher_id ( id, first_name, last_name ), author:author_id ( id, first_name, last_name )")
        .or(`publication_id.eq.${pubId},publication_id_uuid.eq.${pubId}`) // id/uuid 両対応
        .order("author_order", { ascending: true });

      if (error || !rel) {
        if (error) console.error("[fetchAuthorsForPublication]", table, error.message, error.hint, error.details);
        continue;
      }
      if (!rel.length) return [];

      const researcherIds = [...new Set(rel.map((r) => r.researcher_id).filter(Boolean))];
      const authorIds = [...new Set(rel.map((r) => r.author_id).filter(Boolean))];

      let researcherNameById = new Map();
      if (researcherIds.length) {
        try {
          const { data, error } = await supabase
            .from("researchers")
            .select("id, last_name, first_name")
            .in("id", researcherIds);
          if (error) console.error("[fetchAuthorsForPublication:researchers]", error.message, error.hint, error.details);
          if (data) researcherNameById = new Map(data.map((r) => [r.id, [r.last_name, r.first_name].filter(Boolean).join(", ")]));
        } catch (e) {
          console.error("[fetchAuthorsForPublication:researchers:catch]", e);
        }
      }

      let authorNameById = new Map();
      if (!researcherIds.length && authorIds.length) {
        try {
          const { data, error } = await supabase
            .from("authors")
            .select("id, last_name, first_name")
            .in("id", authorIds);
          if (error) console.error("[fetchAuthorsForPublication:authors]", error.message, error.hint, error.details);
          if (data) authorNameById = new Map(data.map((a) => [a.id, [a.last_name, a.first_name].filter(Boolean).join(", ")]));
        } catch (e) {
          console.error("[fetchAuthorsForPublication:authors:catch]", e);
        }
      }

      return rel
        .map((r) => ({
          order: r.author_order ?? 0,
          name:
            (r.researcher_id && researcherNameById.get(r.researcher_id)) ||
            (r.author_id && authorNameById.get(r.author_id)) ||
            "",
        }))
        .sort((a, b) => a.order - b.order)
        .map((x) => x.name)
        .filter(Boolean);
    } catch (e) {
      console.error("[fetchAuthorsForPublication:catch]", table, e);
      // 次の候補にフォールバック
    }
  }
  return [];
}

async function hydrateAuthors(rows) {
  const out = [];
  for (const row of rows) {
    // すでに join で authors が来ている場合はそれを使う
    const names =
      extractAuthorsFromRel(row.publications_authors) ||
      (await fetchAuthorsForPublication(row.id));
    out.push({ ...row, _authors: names });
  }
  return out;
}

async function hydrateJournals(rows, journalMapFromMaster) {
  const ids = [...new Set(rows.map((r) => r.journal_id).filter((v) => isUuid(v) || isIntLike(v)))];
  const jmap =
    journalMapFromMaster ??
    new Map(
      (journalMapFromMaster || []).map((j) => [
        String(j.id),
        j.name_english || j.name_eng || j.name_org || j.name_original || "",
      ])
    );

  if (!ids.length) {
    return rows.map((r) => ({
      ...r,
      _journal_name:
        jmap.get(String(r.journal_id)) ||
        (typeof r.journal_id === "string" ? r.journal_id : ""),
    }));
  }

  return rows.map((r) => {
    const key = String(r.journal_id);
    const resolved = jmap.get(key);
    return {
      ...r,
      _journal_name: resolved ?? (typeof r.journal_id === "string" ? r.journal_id : ""),
    };
  });
}

async function hydrateExtras(rows, journalMapFromMaster) {
  const withJ = await hydrateJournals(rows, journalMapFromMaster);
  const withA = await hydrateAuthors(withJ);
  return withA;
}

/* ---------------------- component ---------------------- */

/**
 * props
 * - label?: string
 * - value?: string | number | null
 * - onChange?: (selectedId: string|number|null) => void
 * - publicationsData?: any[]
 * - renderOptionLabel?: (row)=>string|JSX
 * - required?: boolean | isRequired?: boolean
 * - onRefreshData?: ()=>Promise<void> | onRefreshPublications?: ()=>Promise<void>
 */
export default function PublicationSelector(props) {
  const {
    label = "Source of Original Description",
    value = null,
    onChange,
    publicationsData = [],
    renderOptionLabel,
    onRefreshData,
    onRefreshPublications,
  } = props;

  const required = Boolean(props.required ?? props.isRequired);
  const { journals: journalsMaster } = useMasterData();
  const journalMap = useMemo(
    () =>
      new Map(
        (journalsMaster || []).map((j) => [
          String(j.id),
          j.name_english || j.name_eng || j.name_org || j.name_original || "",
        ])
      ),
    [journalsMaster]
  );
  const [seedOptions, setSeedOptions] = useState([]);
  const [options, setOptions] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [openedAdd, setOpenedAdd] = useState(false);
  const [debouncedInput] = useDebounce(input, 350);
  const lastSearchTermRef = useRef("");
  const selectedOptionRef = useRef(null);
  const suppressSearchRef = useRef(false);

  // ✅ 種データの全量ハイドレート禁止（先頭 MAX_SEED のみ）
  useEffect(() => {
    (async () => {
      if (!Array.isArray(publicationsData) || publicationsData.length === 0) {
        setSeedOptions([]);
        return;
      }
      const MAX_SEED = 50;
      const head = publicationsData.slice(0, MAX_SEED);
      const needHydrate = head.some((p) => !("_authors" in p) || !("_journal_name" in p));
      const seeded = needHydrate ? await hydrateExtras(head, journalMap) : head;
      setSeedOptions(seeded);
    })();
  }, [publicationsData, journalMap]);

  const selectedOption = useMemo(() => {
    if (value == null) return null;
    return (
      options.find((o) => String(o?.id) === String(value)) ||
      seedOptions.find((o) => String(o?.id) === String(value)) ||
      null
    );
  }, [value, options, seedOptions]);

  useEffect(() => {
    selectedOptionRef.current = selectedOption;
  }, [selectedOption]);

  // ✅ value が id のみで詳細が無い場合は単発で取得（URL短い）
  useEffect(() => {
    if (value == null || selectedOption) return;
    (async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("publications")
          .select(BASE_SELECT)
          .eq("id", value)
          .maybeSingle();
        if (error) {
          console.error(`${TAG} fetch by id`, error.message, error.hint, error.details);
          return;
        }
        if (!data) return;
        const hydrated = (await hydrateExtras([data], journalMap))[0];
        setOptions((curr) => (curr.some((p) => String(p.id) === String(hydrated.id)) ? curr : [hydrated, ...curr]));
      } catch (e) {
        console.error(`${TAG} fetch by id (catch)`, e);
      } finally {
        setLoading(false);
      }
    })();
  }, [value, selectedOption, journalMap]);

  const getOptionLabel = useCallback(
    (opt) => {
      if (!opt) return "";
      try {
        const lab = renderOptionLabel ? renderOptionLabel(opt) : defaultLabel(opt);
        return typeof lab === "string" ? stripHtmlAndDecode(lab) : stripHtmlAndDecode(String(lab ?? ""));
      } catch (e) {
        console.warn(`${TAG} getOptionLabel fallback`, e);
        return String(opt?.id ?? "");
      }
    },
    [renderOptionLabel]
  );

  // 選択が確定したら入力欄をラベルで置き換える（検索語を残さない）
  useEffect(() => {
    if (selectedOption) {
      const label = getOptionLabel(selectedOption);
      suppressSearchRef.current = true;
      setInput(label);
    } else {
      suppressSearchRef.current = true;
      setInput("");
    }
  }, [selectedOption, getOptionLabel]);

  // ✅ サーバー検索は最小フィールドのみ + 40件制限 → ハイドレート
  const containsAllTokens = useCallback((row, tokens) => {
    if (!tokens?.length) return true;
    const hay = [
      row._citation,
      row.citation,
      row.title_english,
      row.title,
      row.title_original,
      row.authors_str,
      Array.isArray(row._authors) ? row._authors.join(" ") : "",
      row.journal_hint,
      row.journal_name,
      row.doi,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return tokens.every((t) => hay.includes(t.toLowerCase()));
  }, []);

  const runServerSearch = useCallback(
    async (term) => {
      const t = (term || "").trim();
      if (t.length < 2) {
        setOptions(seedOptions.slice(0, 30));
        return;
      }

      // 同じ語で直前に検索している場合はスキップ（無限ループ防止）
      if (lastSearchTermRef.current === t) {
        return;
      }
      lastSearchTermRef.current = t;

      setLoading(true);
      try {
        console.log(`${TAG} runServerSearch start`, { term: t });
        let results = [];

        // 0) fuzzy RPC (pg_trgm などでスコア付けしたものを返す想定)
        try {
          const { data: fuzzyData, error: fuzzyErr } = await supabase.rpc(RPC_FUZZY, { q: t });
          if (!fuzzyErr && Array.isArray(fuzzyData) && fuzzyData.length) {
            let mapped = fuzzyData.map((row) => ({
              ...row,
              title_english: row.title || row.title_english, // buildCitation で拾えるように
              _authors: parseAuthorsStr(row.authors_str),
              _journal_name: row.journal_name || row.journal_hint || "",
              _citation: row.citation || buildCitation(row),
            }));

            // タイトル/著者/ジャーナルが欠けている行は publications から再度ハイドレート
            const needHydrate = mapped.some(
              (r) =>
                !r._authors?.length ||
                !stripHtmlAndDecode(r.title_english || r.title || r.title_original) ||
                !r._journal_name
            );
            if (needHydrate) {
              const ids = mapped.map((r) => r.id).filter(Boolean);
              if (ids.length) {
                const { data: pRows, error: pErr } = await supabase
                  .from("publications")
                  .select(BASE_SELECT)
                  .in("id", ids);
                if (pErr) {
                  console.warn(`${TAG} fuzzy hydrate pubs error`, pErr.message, pErr.details);
                }
                if (pRows?.length) {
                  const hydrated = await hydrateExtras(pRows, journalMap);
                  const hMap = new Map(hydrated.map((h) => [String(h.id), h]));
                  mapped = mapped.map((r) => {
                    const h = hMap.get(String(r.id));
                    if (!h) return r;
                    const mergedRow = {
                      ...h,
                      ...r,
                      _authors: h._authors?.length ? h._authors : r._authors,
                      _journal_name: h._journal_name || r._journal_name,
                      title_english: h.title_english || r.title_english || r.title,
                      title: h.title_english || r.title || r.title_english,
                    };
                    return {
                      ...mergedRow,
                      _citation: mergedRow._citation || mergedRow.citation || buildCitation(mergedRow),
                    };
                  });
                }
              }
            }

            results = mapped.map((row) => ({
              ...row,
              _citation: row._citation || row.citation || buildCitation(row),
            }));

            console.log(`${TAG} fuzzy rpc`, { term: t, count: fuzzyData.length });
            // fuzzyがヒットした場合はそれを優先し、残りの検索はスキップ
            setOptions(results);
            setLoading(false);
            return;
          }
          // マルチトークンの場合、各トークンで RPC を呼んで全トークン含む行だけ拾う
          const tokens = t.split(/\s+/).filter(Boolean);
          if (tokens.length > 1) {
            const aggResults = [];
            for (const tk of tokens) {
              const { data: fData } = await supabase.rpc(RPC_FUZZY, { q: tk });
              if (Array.isArray(fData)) aggResults.push(...fData);
            }
            if (aggResults.length) {
              const byId = new Map();
              aggResults.forEach((r) => {
                const key = String(r.id);
                if (!byId.has(key)) byId.set(key, r);
              });
              let mergedToken = Array.from(byId.values()).map((row) => ({
                ...row,
                title_english: row.title || row.title_english,
                _authors: parseAuthorsStr(row.authors_str),
                _journal_name: row.journal_name || row.journal_hint || "",
                _citation: row.citation || buildCitation(row),
              }));
              mergedToken = mergedToken.filter((r) => containsAllTokens(r, tokens));
              if (mergedToken.length) {
                setOptions(
                  mergedToken.map((row) => ({
                    ...row,
                    _citation: row._citation || row.citation || buildCitation(row),
                  }))
                );
                setLoading(false);
                console.log(`${TAG} fuzzy multi-token`, { term: t, count: mergedToken.length });
                return;
              }
            }
          }
        } catch (e) {
          console.warn(`${TAG} fuzzy rpc error`, e);
        }

        // 1) ビュー（authors_str など含む）を優先
        try {
            const tokens = t.split(/\s+/).filter(Boolean);
            const { data: viewData, error: viewErr } = await supabase
              .from("v_publication_selector_options")
              .select("id, title, publication_date, authors, authors_str, journal_id, journal_hint, volume, number, page, doi, citation")
              .or(
                buildOrIlike(
                  ["id", "title", "authors_str", "journal_hint", "doi", "citation"],
                  t
                )
              )
              .limit(120);
            if (!viewErr && Array.isArray(viewData)) {
              const filtered =
                tokens.length <= 1
                  ? viewData
                  : viewData.filter((v) => {
                      const hay = [
                        v.citation,
                        v.title,
                        v.authors_str,
                        v.journal_hint,
                        v.doi,
                      ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase();
                      return tokens.every((tok) => hay.includes(tok.toLowerCase()));
                    });

              filtered.forEach((v) =>
                results.push({
                  ...v,
                  title_english: v.title,
                  _authors: parseAuthorsStr(v.authors_str || v.authors),
                  _journal_name: v.journal_hint || "",
                  _citation: v.citation || buildCitation(v),
                })
              );
              console.log(`${TAG} view search`, { term: t, count: filtered.length });
            }
          } catch (e) {
            console.warn(`${TAG} view search error`, e);
          }

        // 2) publications テーブルから基本検索（タイトル/DOI）
        try {
          let query = supabase.from("publications").select(BASE_SELECT).limit(60);
          query = query.or(
            buildOrIlike(
              ["id", "title_english", "title_original", "doi"],
              t
            )
          );
          const { data: pubData, error: pubErr } = await query;
          if (!pubErr && Array.isArray(pubData)) {
            const hydrated = await hydrateExtras(pubData, journalMap);
            results.push(...hydrated);
            console.log(`${TAG} pub search`, { term: t, count: pubData.length });
          }
        } catch (e) {
          console.error(`${TAG} publications search error`, e);
        }

        // 2.5) 著者ビュー（v_pub_author_agg）があれば著者名で publication_id を取得
        try {
          const { data: agg, error: aggErr } = await supabase
            .from("v_pub_author_agg")
            .select("publication_id_text, authors_str")
            .ilike("authors_str", `%${t}%`)
            .limit(100);
          if (!aggErr && Array.isArray(agg) && agg.length) {
            const ids = agg.map((a) => a.publication_id_text).filter(Boolean);
            if (ids.length) {
              const { data: pubsFromAgg } = await supabase
                .from("publications")
                .select(BASE_SELECT)
                .in("id", ids)
                .limit(200);
              if (pubsFromAgg?.length) {
                const hydratedAgg = await hydrateExtras(pubsFromAgg, journalMap);
                results.push(...hydratedAgg);
              }
            }
            console.log(`${TAG} author view search`, { term: t, count: agg.length });
          }
        } catch (e) {
          console.warn(`${TAG} author view search error`, e);
        }

        // 3) 著者名検索: last_name/first_name 部分一致で research→publications_authors→publications を辿る
        try {
          // 3-1) researchers を検索（→ publications_authors.researcher_id）
          const { data: researchers, error: rErr } = await supabase
            .from("researchers")
            .select("id, last_name, first_name")
            .or(buildOrIlike(["last_name", "first_name"], t))
            .limit(50);

          // 3-2) authors テーブルも検索（publication_authors.author_id に紐づく場合）
          let authors = [];
          let aErr = null;
          try {
              const res = await supabase
                .from("author") // schema 上は単数形 author
                .select("id, last_name, first_name")
                .or(buildOrIlike(["last_name", "first_name"], t))
                .limit(50);
            authors = res.data || [];
            aErr = res.error || null;
          } catch (e) {
            aErr = e;
          }

          const pubIds = new Set();
          console.log(`${TAG} author search`, {
            term: t,
            researchers: rErr ? `err:${rErr.code || rErr.message}` : (researchers?.length || 0),
            authors: aErr ? `err:${aErr.code || aErr.message}` : (authors?.length || 0),
          });

          // researcher_id 経由（publications_authors）
          if (!rErr && researchers?.length) {
            const rIds = researchers.map((r) => r.id).filter(Boolean);
            const rel = await selectInBatched({
              table: "publications_authors",
              column: "researcher_id",
              values: rIds,
              select: "publication_id, publication_id_uuid",
              chunkSize: 200,
            });
            (rel || []).forEach((row) => {
              if (row.publication_id) pubIds.add(String(row.publication_id));
              if (row.publication_id_uuid) pubIds.add(String(row.publication_id_uuid));
            });
            console.log(`${TAG} author search via researcher`, { term: t, relCount: rel?.length || 0 });
          }

          // author_id 経由（publication_authors）
          if (!aErr && authors?.length) {
            const aIds = authors.map((a) => a.id).filter(Boolean);
            const rel2 = await selectInBatched({
              table: "publication_authors",
              column: "author_id",
              values: aIds,
              select: "publication_id, publication_id_uuid",
              chunkSize: 200,
            });
            (rel2 || []).forEach((row) => {
              if (row.publication_id) pubIds.add(String(row.publication_id));
              if (row.publication_id_uuid) pubIds.add(String(row.publication_id_uuid));
            });
            console.log(`${TAG} author search via author`, { term: t, relCount: rel2?.length || 0 });
          }

          const idList = Array.from(pubIds);
          if (idList.length) {
            const extraRows = [];
            const textIds = idList.filter((v) => typeof v === "string");
            if (textIds.length) {
              const { data: p1 } = await supabase
                .from("publications")
                .select(BASE_SELECT)
                .in("id", textIds)
                .limit(200);
              if (p1?.length) extraRows.push(...p1);
            }
            const uuidIds = idList.filter((v) => isUuid(v));
            if (uuidIds.length) {
              const { data: p2 } = await supabase
                .from("publications")
                .select(BASE_SELECT)
                .in("id_uuid", uuidIds)
                .limit(200);
              if (p2?.length) extraRows.push(...p2);
            }
            if (extraRows.length) {
                const hydratedExtra = await hydrateExtras(extraRows, journalMap);
                results.push(...hydratedExtra);
                console.log(`${TAG} author search pubs fetched`, { term: t, pubs: extraRows.length });
              }
            }
        } catch (e) {
          console.warn(`${TAG} author-search fallback error`, e);
        }

        // 重複除去
        const byId = new Map();
        results.forEach((row) => {
          if (!row?.id) return;
          byId.set(String(row.id), row);
        });
        let merged = Array.from(byId.values());

        // 追加ハイドレート: authors が無い行に対して publications_authors / publication_authors をまとめて取得
        const needRows = merged.filter((r) => {
          const a = Array.isArray(r._authors) ? r._authors : parseAuthorsStr(r.authors_str);
          return !a || a.length === 0;
        });
        if (needRows.length) {
          const needIds = needRows.map((r) => r.id).filter(Boolean);
          const needUuid = needRows.map((r) => r.id_uuid).filter(Boolean);
          console.log(`${TAG} hydrate missing authors`, {
            term: t,
            rows: needRows.length,
            ids: needIds.length,
            uuids: needUuid.length,
          });
          try {
            const rels = [];
            // publications_authors by id
            if (needIds.length) {
              const relId = await selectInBatched({
                table: "publications_authors",
                column: "publication_id",
                values: needIds,
                select: `publication_id, publication_id_uuid, author_order, researcher:researcher_id ( id, first_name, last_name ), author:author_id ( id, first_name, last_name )`,
                chunkSize: 200,
                order: { column: "author_order", ascending: true },
              });
              rels.push(...(relId || []));
            }
            // publications_authors by uuid
            if (needUuid.length) {
              const relUuid = await selectInBatched({
                table: "publications_authors",
                column: "publication_id_uuid",
                values: needUuid,
                select: `publication_id, publication_id_uuid, author_order, researcher:researcher_id ( id, first_name, last_name ), author:author_id ( id, first_name, last_name )`,
                chunkSize: 200,
                order: { column: "author_order", ascending: true },
              });
              rels.push(...(relUuid || []));
            }
            // publication_authors by author_id (別テーブル)
            if (needIds.length) {
              const relId2 = await selectInBatched({
                table: "publication_authors",
                column: "publication_id",
                values: needIds,
                select: `publication_id, publication_id_uuid, author_order, author_id`,
                chunkSize: 200,
                order: { column: "author_order", ascending: true },
              });
              rels.push(...(relId2 || []));
            }
            if (needUuid.length) {
              const relUuid2 = await selectInBatched({
                table: "publication_authors",
                column: "publication_id_uuid",
                values: needUuid,
                select: `publication_id, publication_id_uuid, author_order, author_id`,
                chunkSize: 200,
                order: { column: "author_order", ascending: true },
              });
              rels.push(...(relUuid2 || []));
            }

            // author_id→氏名解決
            const authorIds = [...new Set(rels.map((r) => r.author_id).filter(Boolean))];
            const authorMap = new Map();
            if (authorIds.length) {
              const { data: aRows } = await supabase
                .from("author")
                .select("id, first_name, last_name")
                .in("id", authorIds);
              (aRows || []).forEach((a) =>
                authorMap.set(a.id, [a.last_name, a.first_name].filter(Boolean).join(", "))
              );
            }

            const relByPub = rels.reduce((m, r) => {
              const key = r.publication_id || r.publication_id_uuid;
              if (!key) return m;
              const arr = m.get(String(key)) || [];
              const name =
                (r.researcher && [r.researcher.last_name, r.researcher.first_name].filter(Boolean).join(", ")) ||
                (r.author_id && authorMap.get(r.author_id)) ||
                null;
              arr.push({ ...r, _name: name });
              m.set(String(key), arr);
              return m;
            }, new Map());

            merged = merged.map((row) => {
              const rels = relByPub.get(String(row.id)) || relByPub.get(String(row.id_uuid)) || [];
              const names = rels
                .sort((a, b) => (a.author_order ?? 0) - (b.author_order ?? 0))
                .map((x) => x._name)
                .filter(Boolean);
              return names.length ? { ...row, _authors: names } : row;
            });
          } catch (e) {
            console.warn(`${TAG} hydrate missing authors failed`, e);
          }
        }

        // v_pub_author_agg から authors_str を補完（不足時のバックアップ）
        try {
          const idsForAgg = merged.map((r) => r.id).filter(Boolean);
          if (idsForAgg.length) {
            const aggRows = await selectInBatched({
              table: "v_pub_author_agg",
              column: "publication_id_text",
              values: idsForAgg,
              select: "publication_id_text, authors_str",
              chunkSize: 200,
            });
            const aggMap = new Map();
            (aggRows || []).forEach((row) => aggMap.set(String(row.publication_id_text), row.authors_str || ""));
            merged = merged.map((row) => {
              const currentAuthors = Array.isArray(row._authors) ? row._authors : parseAuthorsStr(row.authors_str);
              if (currentAuthors.length) return row;
              const aStr = aggMap.get(String(row.id)) || "";
              const parsed = parseAuthorsStr(aStr);
              return parsed.length ? { ...row, _authors: parsed, authors_str: aStr } : row;
            });
          }
        } catch (e) {
          console.warn(`${TAG} hydrate authors_str via agg failed`, e);
        }

        const mergedWithCitation = merged.map((row) => ({
          ...row,
          _citation: row._citation || row.citation || buildCitation(row),
        }));

        console.log(`${TAG} merged results`, { term: t, merged: mergedWithCitation.length });

        const next = [...mergedWithCitation];
        const sel = selectedOptionRef.current;
        if (value != null && sel && !mergedWithCitation.some((r) => String(r.id) === String(value))) {
          next.unshift(sel);
        }
        setOptions(next);
      } catch (e) {
        console.error(`${TAG} search (catch)`, e);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [seedOptions, getOptionLabel, value, journalMap, containsAllTokens]
  );

  useEffect(() => {
    const t = (debouncedInput || "").trim();
    if (suppressSearchRef.current) {
      suppressSearchRef.current = false;
      lastSearchTermRef.current = t || "";
      return;
    }
    if (t.length >= 2) {
      runServerSearch(t);
    } else {
      // 入力が短いときは検索を走らせず、種オプションに戻す
      lastSearchTermRef.current = ""; // リセット
      setLoading(false);
      setOptions(seedOptions.slice(0, 30));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInput]);

  const handleCloseAdd = async (didAdd) => {
    setOpenedAdd(false);
    const refresher = onRefreshData || onRefreshPublications;
    try {
      if (didAdd && refresher) {
        await refresher();
        setOptions([]); // seedOptions 経由で再反映
      }
    } catch (e) {
      console.error(`${TAG} onClose(add)`, e);
    }
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: "100%" }}>
        <Autocomplete
          options={options}
          value={selectedOption}
          onChange={(_, val) => onChange?.(val?.id ?? null)}
          inputValue={input}
          onInputChange={(_, v, reason) => {
            // フォーカス時などの自動リセットで全ラベルが流入するのを防ぎ、実入力のみを採用
            if (reason === "input") {
              setInput(v);
            } else if (reason === "clear") {
              setInput("");
            }
          }}
          isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
          filterOptions={(opts) => opts} // サーバー結果をそのまま表示（クライアント側フィルタ無効化）
          getOptionLabel={getOptionLabel}
          loading={loading}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <Box component="span" sx={{ display: "block", wordBreak: "break-word", whiteSpace: "normal" }}>
                  {getOptionLabel(option)}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={required ? `${label} (Required)` : `${label} (Optional)`}
              required={required}
              margin="dense"
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ flexGrow: 1 }}
          ListboxProps={{ style: { maxHeight: 360 } }}
          noOptionsText={debouncedInput?.trim()?.length >= 2 ? "No results" : "Type 2+ characters to search"}
        />

        <Button size="small" startIcon={<AddIcon />} onClick={() => setOpenedAdd(true)} sx={{ mt: "15px" }}>
          + Add
        </Button>
      </Stack>

      {/* ★ Upsert を直接呼び出し（追加モード） */}
      {openedAdd && <DialogPublicationUpsert open mode="add" onClose={handleCloseAdd} />}
    </>
  );
}
